import { Button, Col, Dropdown, Form, Modal, Row, Table } from 'react-bootstrap';
import platformsSignal, {
  editPlatformTranchesSignal,
} from '../Platforms.signal';
import {
  formatTrancheAmountInput,
  handleAddTranch,
  handleEditTrancheSubmit,
  handleNewTrancheChange,
  handleRemoveTrancheSubmit,
  handleTrancheAmountChange,
  handleTrancheDateChange,
  toggleEditTranche,
  toggleRemoveTrancheConfirmModal,
  validateTrancheEditIsWithinGracePeriod,
} from '../Platforms.helpers';
import currencyOptions from 'components/global/Constant/currencyOptions';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import ModalWrapper from 'components/global/ModalWrapper/ModalWrapper';
import loaderSignal from 'signals/Loader.signal';
import { getConfig } from 'config/config';
import $appSettings from 'signals/AppSettings.signal';

const EditPlatformTranchesTab = () => {
  const {
    tranches,
    newTranche,
    editTrancheId,
    removeTrancheConfirmModalVisible,
  } = editPlatformTranchesSignal.value;
  const { TRANCHE_GRACE_PERIODS } = $appSettings.value.constants;
  const config = getConfig('APP_CONFIG');
  const appId: string = config.appId;
  const gracePeriod = (TRANCHE_GRACE_PERIODS as Record<string, number>)[appId] || TRANCHE_GRACE_PERIODS.dev;

  const { platformBeingEdited } = platformsSignal.value;

  // @ts-ignore
  return (
    <>
      <h5 className="ml-16 ml-lg-64 mt-32">Platform Funding</h5>
      <div className="px-16 px-lg-64 mt-16">
        <p className="mb-8">
          <b>Platform funding</b> is managed through tranches. Add a tranche to allocate funds to this platform.
        </p>
        <p className="mb-8">
          <b>Remaining funds</b>:{' '}
          {(platformBeingEdited?.stats?.remaining || 0).toLocaleString('en-US', currencyOptions.currencyUS)}
        </p>
        {(!platformBeingEdited?.stats?.remaining || platformBeingEdited.stats.remaining <= 0) && (
          <div className="alert alert-warning" role="alert">
            <strong>No funds allocated yet.</strong> Add a tranche below to fund this platform. Programs cannot be created until the platform has available funds.
          </div>
        )}
      </div>
      <h6 className="ml-16 ml-lg-64 mt-24">Create a New Tranche</h6>
      <Row className="my-3 px-16 px-lg-64">
        <Col xs={12} lg>
          <Form.Group className="form-group mb-0">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="text"
              className="form-control"
              value={
                newTranche.amount === '' || newTranche.amount === undefined
                  ? ''
                  : formatTrancheAmountInput(newTranche.amount)
              }
              name="amount"
              onChange={(e) =>
                handleNewTrancheChange(e.target.name, e.target.value)
              }
            />
          </Form.Group>
        </Col>
        <Col className="mt-16 mt-lg-0">
          {/* guarantees same height
          <p>
            <b>Remaining funds</b>: $
            {platformBeingEdited?.stats.remaining.toLocaleString()}
          </p> */}
          <Form.Group className="form-group mb-0">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={newTranche.date}
              name="date"
              onChange={(e) =>
                handleNewTrancheChange(e.target.name, e.target.value)
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="px-32 px-lg-80 justify-content-end">
        <Button
          disabled={
            loaderSignal.value.isContentLoading ||
            !newTranche.amount ||
            newTranche.amount === '' ||
            !newTranche.date ||
            newTranche.date === ''
          }
          variant="outline-primary"
          className="rounded"
          onClick={() => handleAddTranch()}
        >
          Save Tranche
        </Button>
      </Row>
      <Row className="mt-16 px-16 px-lg-64">
        <Col>
        <p className="small text-muted">
          <strong>Note:</strong> Tranches can only be edited or removed within {gracePeriod} hours after creation for data integrity.
        </p>
          <Table responsive>
            <thead>
              <tr>
                <th>Amount</th>
                <th>Date</th>
                <th> </th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {tranches.map((tranche) => {
                const remainingTimeToModify = validateTrancheEditIsWithinGracePeriod(tranche.createdDate);
                if (editTrancheId === tranche.id) {
                  return (
                    <tr key={tranche.id}>
                      <td>
                        <Form.Group className="form-group mb-0">
                          <Form.Control
                            value={formatTrancheAmountInput(tranche.amount)}
                            onChange={(e) =>
                              handleTrancheAmountChange(
                                e.target.value,
                                tranche.id
                              )
                            }
                          />
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Group className="form-group mb-0">
                          <Form.Control
                            type="date"
                            disabled={true}
                            value={
                              new Date(tranche.date).toISOString().split('T')[0]
                            }
                            onChange={(e) =>
                              handleTrancheDateChange(
                                e.target.value,
                                tranche.id
                              )
                            }
                          />
                        </Form.Group>
                      </td>
                      <td>
                        <Button
                          className="rounded mr-8"
                          size="sm"
                          variant="outline-primary"
                          onClick={handleEditTrancheSubmit}
                          disabled={!remainingTimeToModify}
                        >
                          Save Changes
                        </Button>
                        <Button
                          className="rounded"
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => toggleEditTranche()}
                          disabled={!remainingTimeToModify}
                        >
                          Cancel
                        </Button>
                      </td>
                      <td className="small text-muted">
                        {remainingTimeToModify ? `${remainingTimeToModify} hours left to edit` : "Edit period expired"}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={tranche.id}>
                    <td>
                      {Number(tranche.amount).toLocaleString(
                        'en-US',
                        currencyOptions.currencyUS
                      )}
                    </td>
                    <td>
                      {new Date(tranche.date).toLocaleDateString('en-US', {
                        timeZone: 'UTC',
                      })}
                    </td>
                    <td align="right">
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-primary" size="sm">Actions</Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => toggleEditTranche(tranche.id)}
                            disabled={!remainingTimeToModify}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              toggleRemoveTrancheConfirmModal(tranche.id)
                            }
                            disabled={!remainingTimeToModify}
                          >
                            Remove
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                    <td className="small text-muted">
                      {remainingTimeToModify ? `${remainingTimeToModify} hours left to edit` : "Edit period expired"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Col>
      </Row>
      <ModalWrapper
        centered
        show={removeTrancheConfirmModalVisible}
        onHide={toggleRemoveTrancheConfirmModal}
        className="upper-modal"
        backdropClassName="upper-modal-backdrop"
        onExit={null}
        onExited={null}
        size={null}
      >
        <Modal.Header className="justify-content-end pb-0 border-bottom-0">
          <CloseModalButton
            onCloseModal={() => toggleRemoveTrancheConfirmModal()}
          />
        </Modal.Header>
        <Modal.Body className="pt-0">
          <p>Are you sure you want to remove this tranche?</p>
          <Row className="px-16 justify-content-end">
            <Button
              className="rounded mr-8"
              variant="outline-secondary"
              size="sm"
              onClick={() => toggleRemoveTrancheConfirmModal()}
            >
              Cancel
            </Button>
            <Button
              className="rounded"
              size="sm"
              onClick={handleRemoveTrancheSubmit}
            >
              Confirm
            </Button>
          </Row>
        </Modal.Body>
      </ModalWrapper>
    </>
  );
};

export default EditPlatformTranchesTab;
